<script setup lang="ts">
/**
 * WhatsAppButton — pendant exact de `CalendlyButton`, pour le second canal de
 * contact. Même géométrie, même comportement de dimensionnement par le
 * parent : les deux s'empilent sans réglage dans les cartes « Commencer avec
 * Donoor » du header (panneau déroulant et menu mobile).
 *
 * Seule la couleur change — vert WhatsApp (#25D366) au lieu du bleu Calendly
 * — pour que le service se reconnaisse sans lire le libellé.
 *
 * Clic → ouvre WhatsApp avec le message de contact pré-rempli
 * (`openWhatsAppContact`, numéro centralisé dans `~/utils/whatsapp`).
 * Karim 2026-09-10.
 */
import { openWhatsAppContact } from "~/utils/whatsapp";

withDefaults(
  defineProps<{
    label?: string;
    /** Taille du rond icône (classes tailwind). */
    iconWrapperClass?: string;
  }>(),
  {
    label: "Nous contacter",
    iconWrapperClass: "w-8 h-8",
  },
);
</script>

<template>
  <button
    type="button"
    class="wa-btn group/btn relative inline-flex items-center justify-center gap-3 bg-white text-gray-800 font-medium rounded-full transition-all duration-300 hover:shadow-lg hover:shadow-[#25D366]/20 cursor-pointer"
    @click="openWhatsAppContact()"
  >
    <!-- Bordure dégradée animée (vert WhatsApp) -->
    <span class="wa-border"></span>
    <!-- Icône sur rond dégradé vert -->
    <span
      class="relative z-10 flex items-center justify-center rounded-full bg-gradient-to-br from-[#25D366] to-[#12b855]"
      :class="iconWrapperClass"
    >
      <UIcon name="i-simple-icons-whatsapp" class="w-4 h-4 text-white" />
    </span>
    <span class="relative z-10">{{ label }}</span>
  </button>
</template>

<style scoped>
.wa-btn {
  position: relative;
  overflow: hidden;
  border: 2px solid transparent;
  background-clip: padding-box;
}

.wa-border {
  position: absolute;
  inset: -2px;
  border-radius: 9999px;
  background: linear-gradient(
    90deg,
    #25d366,
    #4ae08a,
    #0f9d58,
    #4ae08a,
    #25d366
  );
  background-size: 300% 100%;
  animation: wa-gradient 3s ease infinite;
  z-index: 0;
}

.wa-btn::before {
  content: "";
  position: absolute;
  inset: 2px;
  background: white;
  border-radius: 9999px;
  z-index: 1;
}

@keyframes wa-gradient {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

.wa-btn:hover .wa-border {
  animation-duration: 1.5s;
}

/* Accessibilité : sans mouvement, la bordure verte reste — c'est la couleur
   qui identifie le service, l'animation ne fait que l'appuyer. */
@media (prefers-reduced-motion: reduce) {
  .wa-border {
    animation: none;
  }
}
</style>
